/**
 * "Iniciar projeto" — from an idea to the first code, in a conversation (docs/refactor/13-iniciar-projeto.md).
 *
 * The pure parts of the flow, kept here so they are tested without a screen:
 *
 *  - reading what the catalog's start.* prompts answer: the interview's JSON between
 *    <vephus-interview> tags, the proposal and its <vephus-proposal> summary, the review's verdict,
 *    the first specification's path;
 *  - the answers: what the AI assumed (only to confirm, "Confirmar todas"), what the person answered,
 *    skipped, or did not know — "não sei" takes the safest simple default the interview proposed and
 *    is marked as decided by the AI, and goes to the pending list with the skipped ones that matter
 *    for launch;
 *  - filling a prompt's placeholders so the whole thing still goes out as one work order;
 *  - where it is kept: a document in the team's documentation, projetos/<name>/entrevista.md, with
 *    the data in a fenced block at its end — the team can read it, the flow resumes from it (on
 *    another device too: started on the web, finished in the desktop app), and "O que falta" reads
 *    the store and domain answers from it instead of asking again.
 */

import type { I18n } from './i18n'

type Translate = I18n['t']

// ------------------------------------------------------------------------------ the interview

export interface InterviewQuestion {
  id: string
  text: string
  options: string[]
  /** What the AI assumed from the description, or null when it is for the person to answer. */
  answer: string | null
  assumed: boolean
  /** One line: why the AI assumed it, or why the question matters. */
  why: string
  /** The safest simple choice, taken when the person does not know. */
  safeDefault: string
  mattersForLaunch: boolean
}

export interface Interview {
  name: string
  summary: string
  questions: InterviewQuestion[]
}

const MAX_QUESTIONS = 24

function str(value: unknown, max = 600): string {
  return typeof value === 'string' ? value.trim().slice(0, max) : ''
}

/** The JSON between two tags, tolerating a code fence the agent put around it. */
function block(text: string, tag: string): string | null {
  const match = new RegExp(`<${tag}>\\s*([\\s\\S]*?)\\s*</${tag}>`, 'i').exec(text)
  if (!match) return null
  return match[1]!.replace(/^```[a-z]*\s*/i, '').replace(/\s*```$/, '').trim()
}

/** The interview the agent answered, or null when the reply has none the app can read. */
export function interviewOf(text: string): Interview | null {
  const json = block(text, 'vephus-interview')
  if (!json) return null
  let parsed: unknown
  try {
    parsed = JSON.parse(json)
  } catch {
    return null
  }
  if (!parsed || typeof parsed !== 'object') return null
  const root = parsed as Record<string, unknown>
  const seen = new Set<string>()
  const questions: InterviewQuestion[] = []
  for (const raw of Array.isArray(root.questions) ? root.questions : []) {
    if (!raw || typeof raw !== 'object') continue
    const q = raw as Record<string, unknown>
    const text = str(q.text)
    let id = str(q.id, 40).toLowerCase().replace(/[^a-z0-9_-]/g, '') || `q${questions.length + 1}`
    if (!text) continue
    while (seen.has(id)) id = `${id}-${questions.length + 1}`
    seen.add(id)
    const answer = str(q.answer) || null
    questions.push({
      id,
      text,
      options: (Array.isArray(q.options) ? q.options : []).map((o) => str(o, 200)).filter(Boolean).slice(0, 6),
      answer,
      assumed: q.assumed === true && answer !== null,
      why: str(q.why, 300),
      safeDefault: str(q.safe_default) || str(q.safeDefault),
      mattersForLaunch: q.matters_for_launch === true || q.mattersForLaunch === true,
    })
    if (questions.length >= MAX_QUESTIONS) break
  }
  if (questions.length === 0) return null
  return { name: slugify(str(root.name, 80)), summary: str(root.summary, 400), questions }
}

// ------------------------------------------------------------------------------ the answers

/**
 * - open: for the person to answer;
 * - assumed: the AI's answer, not confirmed yet;
 * - confirmed: the AI's answer, confirmed by the person;
 * - answered: the person's own answer;
 * - skipped: left out of the proposal;
 * - unknown: "não sei" — the safest simple default, decided by the AI.
 */
export type AnswerStatus = 'open' | 'assumed' | 'confirmed' | 'answered' | 'skipped' | 'unknown'

export interface Answer {
  status: AnswerStatus
  value: string | null
}

export type Answers = Record<string, Answer>

export function initialAnswers(interview: Interview): Answers {
  return Object.fromEntries(
    interview.questions.map((q) => [q.id, q.assumed && q.answer ? { status: 'assumed', value: q.answer } : { status: 'open', value: null }]),
  )
}

/** "Confirmar todas": every answer the AI assumed, confirmed as it is. */
export function confirmAll(answers: Answers): Answers {
  return Object.fromEntries(Object.entries(answers).map(([id, a]) => [id, a.status === 'assumed' ? { ...a, status: 'confirmed' } : a]))
}

export function confirmOne(answers: Answers, id: string): Answers {
  const a = answers[id]
  return a?.status === 'assumed' ? { ...answers, [id]: { ...a, status: 'confirmed' } } : answers
}

/** The person's answer: clicking the AI's own suggestion again counts as confirming it. */
export function answerOne(answers: Answers, question: InterviewQuestion, value: string): Answers {
  const text = value.trim().slice(0, 600)
  if (!text) return answers
  const same = question.assumed && question.answer !== null && text === question.answer
  return { ...answers, [question.id]: { status: same ? 'confirmed' : 'answered', value: text } }
}

export function skipOne(answers: Answers, id: string): Answers {
  return { ...answers, [id]: { status: 'skipped', value: null } }
}

/** "Não sei responder": the safest simple default, marked as decided by the AI. */
export function dontKnow(answers: Answers, question: InterviewQuestion): Answers {
  return { ...answers, [question.id]: { status: 'unknown', value: question.safeDefault || null } }
}

/** How many still wait for the person: open ones, and the AI's not yet confirmed. */
export function unresolved(answers: Answers): number {
  return Object.values(answers).filter((a) => a.status === 'open' || a.status === 'assumed').length
}

export function assumedCount(answers: Answers): number {
  return Object.values(answers).filter((a) => a.status === 'assumed').length
}

const STATUS_FOR_PROMPT: Record<AnswerStatus, string> = {
  open: 'not answered',
  assumed: 'AI suggestion, not confirmed',
  confirmed: 'AI suggestion, confirmed by the person',
  answered: 'answered by the person',
  skipped: 'skipped',
  unknown: 'decided by the AI: the person did not know',
}

/** The answers as the proposal and the documentation prompts receive them. */
export function answersText(interview: Interview, answers: Answers): string {
  return interview.questions
    .map((q) => {
      const a = answers[q.id] ?? { status: 'open', value: null }
      const value = a.status === 'skipped' ? '—' : a.value ?? '—'
      return `- [${q.id}] ${q.text} → ${value} (${STATUS_FOR_PROMPT[a.status]})`
    })
    .join('\n')
}

/** What goes to PENDENCIAS.md: every "não sei" (decided by the AI), and what was skipped but matters for launch. */
export function pendingItems(interview: Interview, answers: Answers, t: Translate): string[] {
  const items: string[] = []
  for (const q of interview.questions) {
    const a = answers[q.id]
    if (a?.status === 'unknown') items.push(t('startProject.pending.decided', { question: q.text, value: a.value ?? '—' }))
    else if (a?.status === 'skipped' && q.mattersForLaunch) items.push(t('startProject.pending.skipped', { question: q.text }))
  }
  return items
}

// ------------------------------------------------------------------------------ proposal and review

export interface ProposalPart {
  id: string
  name: string
  kind: string
  stack: string
  summary: string
}

export interface ProposalSummary {
  name: string
  parts: ProposalPart[]
  hosting: string
  deferred: string[]
}

/** The proposal as Markdown, and its JSON summary when the agent wrote one. */
export function proposalOf(text: string): { markdown: string; summary: ProposalSummary | null } {
  const json = block(text, 'vephus-proposal')
  let summary: ProposalSummary | null = null
  if (json) {
    try {
      const root = JSON.parse(json) as Record<string, unknown>
      summary = {
        name: slugify(str(root.name, 80)),
        parts: (Array.isArray(root.parts) ? root.parts : [])
          .filter((p): p is Record<string, unknown> => Boolean(p) && typeof p === 'object')
          .map((p) => ({ id: str(p.id, 40), name: str(p.name, 120), kind: str(p.kind, 20), stack: str(p.stack, 300), summary: str(p.summary, 400) }))
          .filter((p) => p.name)
          .slice(0, 12),
        hosting: str(root.hosting, 600),
        deferred: (Array.isArray(root.deferred) ? root.deferred : []).map((d) => str(d, 300)).filter(Boolean).slice(0, 20),
      }
    } catch {
      summary = null
    }
  }
  const markdown = text
    .replace(/<vephus-proposal>[\s\S]*?<\/vephus-proposal>/gi, '')
    .replace(/DEVPULSE-STATUS:.*$/gim, '')
    .trim()
  return { markdown, summary }
}

/** The review: whether nothing must change before starting, and the rest as Markdown. */
export function reviewOf(text: string): { ok: boolean | null; markdown: string } {
  const verdict = /VEPHUS-REVIEW:\s*(ok|changes)/i.exec(text)
  const markdown = text.replace(/^VEPHUS-REVIEW:.*$/gim, '').replace(/DEVPULSE-STATUS:.*$/gim, '').trim()
  return { ok: verdict ? verdict[1]!.toLowerCase() === 'ok' : null, markdown }
}

/** The first specification the documentation prompt wrote, when it said which. */
export function firstSpecOf(text: string): string | null {
  const match = /VEPHUS-FIRST-SPEC:\s*`?([^\s`]+\.md)/i.exec(text)
  const path = match?.[1] ?? null
  return path && !path.includes('..') && !path.startsWith('/') ? path : null
}

// ------------------------------------------------------------------------------ prompts

/** What a work order takes (RemoteControl.MaxCommandLength - 1000), with room for the status-line rule. */
export const MAX_INSTRUCTIONS = 10_800

const CUT = '\n(…cut here: the whole text is in Vephus\'s documentation.)'

/**
 * Fills `{{placeholders}}`, cutting the longest values first so the prompt still fits in one work
 * order. Placeholders with no value become empty; `{{rules:…}}` were already expanded by the server.
 */
export function fitPrompt(body: string, values: Record<string, string>, max = MAX_INSTRUCTIONS): string {
  const names = [...new Set([...body.matchAll(/\{\{([a-z_]+)\}\}/g)].map((m) => m[1]!))]
  const filled: Record<string, string> = Object.fromEntries(names.map((n) => [n, (values[n] ?? '').trim()]))
  const fixed = body.replace(/\{\{([a-z_]+)\}\}/g, '').length
  const uses = (n: string) => body.split(`{{${n}}}`).length - 1
  const total = () => names.reduce((sum, n) => sum + filled[n]!.length * uses(n), fixed)
  let guard = 0
  while (total() > max && guard++ < 50) {
    const longest = names.reduce((a, b) => (filled[a]!.length * uses(a) >= filled[b]!.length * uses(b) ? a : b))
    const excess = total() - max
    const keep = Math.max(0, filled[longest]!.length - Math.ceil(excess / uses(longest)) - CUT.length)
    if (keep === 0 && filled[longest]!.length === 0) break
    filled[longest] = keep > 0 ? filled[longest]!.slice(0, keep) + CUT : ''
  }
  return body.replace(/\{\{([a-z_]+)\}\}/g, (_, n: string) => filled[n] ?? '')
}

// ------------------------------------------------------------------------------ names

/** A folder name from any text: lowercase, ASCII, hyphens; at most 40 characters. */
export function slugify(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40)
    .replace(/-+$/, '')
}

/** The same rule the desktop applies when it makes the folder (workspace.rs, valid_project_name). */
export function validProjectName(name: string): boolean {
  return /^[A-Za-z0-9][A-Za-z0-9_-]{0,59}$/.test(name) && !/^(con|prn|aux|nul|com[1-9]|lpt[1-9])$/i.test(name)
}

// ------------------------------------------------------------------------------ the draft

export type StartStep = 'idea' | 'questions' | 'proposal' | 'start'
/** What the flow is waiting for an agent to answer. */
export type Waiting = 'interview' | 'proposal' | 'review' | 'docs' | 'first' | null

export interface StartDraft {
  v: 1
  step: StartStep
  idea: string
  /** The folder and repository name, from the interview's suggestion until the person changes it. */
  name: string
  interview: Interview | null
  answers: Answers
  proposal: string | null
  summary: ProposalSummary | null
  review: string | null
  reviewOk: boolean | null
  waiting: Waiting
  repositoryId: string | null
  repositoryName: string | null
  docsSent: boolean
  firstSpec: string | null
  firstSent: boolean
  /** The first development's report, once it ended. */
  result: string | null
}

export function emptyDraft(): StartDraft {
  return {
    v: 1, step: 'idea', idea: '', name: '', interview: null, answers: {}, proposal: null, summary: null, review: null, reviewOk: null,
    waiting: null, repositoryId: null, repositoryName: null, docsSent: false, firstSpec: null, firstSent: false, result: null,
  }
}

const DRAFT_KEY = 'devpulse.startProject.draft'

export function loadDraft(): StartDraft {
  try {
    const parsed = JSON.parse(localStorage.getItem(DRAFT_KEY) ?? 'null') as StartDraft | null
    return parsed?.v === 1 ? { ...emptyDraft(), ...parsed } : emptyDraft()
  } catch {
    return emptyDraft()
  }
}

export function saveDraft(draft: StartDraft) {
  try {
    localStorage.setItem(DRAFT_KEY, JSON.stringify(draft))
  } catch {
    // Private storage: the document in the team's documentation still has it.
  }
}

export function clearDraft() {
  try {
    localStorage.removeItem(DRAFT_KEY)
  } catch {
    // Nothing to clear.
  }
}

// ------------------------------------------------------------------------------ the documents

export const START_FOLDER = 'projetos'

export function interviewDocPath(name: string): string {
  return `${START_FOLDER}/${name}/entrevista.md`
}

export function proposalDocPath(name: string): string {
  return `${START_FOLDER}/${name}/proposta.md`
}

/** Whether a documentation path is a project interview (to list the ones to continue). */
export function isInterviewDoc(path: string): boolean {
  return /^projetos\/[^/]+\/entrevista\.md$/.test(path)
}

const DATA_FENCE = /```json vephus-start\n([\s\S]*?)\n```/

/** The interview as the team reads it, with the flow's data at its end to resume from. */
export function interviewDoc(draft: StartDraft, t: Translate): string {
  const lines = [`# ${draft.name || t('startProject.title')} — ${t('startProject.doc.interview')}`, '', `## ${t('startProject.step.idea')}`, '', draft.idea.trim(), '']
  if (draft.interview) {
    lines.push(`## ${t('startProject.step.questions')}`, '')
    for (const q of draft.interview.questions) {
      const a = draft.answers[q.id] ?? { status: 'open', value: null }
      const value = a.status === 'skipped' ? '—' : a.value ?? '—'
      lines.push(`- **${q.text}** ${value} _(${t(`startProject.status.${a.status}`)})_`)
    }
    lines.push('')
  }
  const data: StartDraft = { ...draft, waiting: null }
  lines.push(`## ${t('startProject.doc.data')}`, '', '```json vephus-start', JSON.stringify(data), '```', '')
  return lines.join('\n')
}

/** The proposal and its review, as the team reads them. */
export function proposalDoc(draft: StartDraft, t: Translate): string {
  return [
    `# ${draft.name || t('startProject.title')} — ${t('startProject.step.proposal')}`,
    '',
    draft.proposal?.trim() ?? '',
    '',
    `## ${t('startProject.review.title')}`,
    '',
    draft.review?.trim() ?? '—',
    '',
  ].join('\n')
}

/** The flow's data from an interview document, or null when it has none. */
export function draftFromDoc(content: string): StartDraft | null {
  const match = DATA_FENCE.exec(content)
  if (!match) return null
  try {
    const parsed = JSON.parse(match[1]!) as StartDraft
    return parsed?.v === 1 ? { ...emptyDraft(), ...parsed, waiting: null } : null
  } catch {
    return null
  }
}

/**
 * What "O que falta" asks — an app in a store, a domain — when the project's interview already
 * answered it: the sentence its prompt takes, or null to ask the person.
 */
export function launchAnswers(content: string): { store: string | null; domain: string | null } {
  const draft = draftFromDoc(content)
  if (!draft?.interview) return { store: null, domain: null }
  const said = (id: string): string | null => {
    const a = draft.answers[id]
    return a && (a.status === 'confirmed' || a.status === 'answered' || a.status === 'unknown') && a.value ? a.value : null
  }
  const stores = said('stores')
  const mobile = said('mobile')
  const domain = said('domain')
  return {
    store: stores
      ? `in the project's interview, about the app stores, they answered: "${stores}"`
      : mobile
        ? `in the project's interview, about a mobile app, they answered: "${mobile}"`
        : null,
    domain: domain ? `in the project's interview, about a domain, they answered: "${domain}"` : null,
  }
}
